import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Sprint 30 — Browser notifications for new (likely-appointment) emails.
// Polls /api/email/notifications every 60s when opt-in is enabled and IMAP is
// configured. Auto-triggers a background /api/email/sync every 5 min so that
// notifications fire even without manual sync.
const OPTIN_KEY = "stellar.notifications.enabled";
const LAST_CHECK_KEY = "stellar.notifications.lastCheck";
const SEEN_IDS_KEY = "stellar.notifications.seenIds";
const POLL_MS = 60 * 1000;
const AUTO_SYNC_MS = 5 * 60 * 1000;

function getSeenIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_IDS_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveSeenIds(set) {
  // Cap at 200 IDs so the list doesn't grow unbounded.
  const arr = Array.from(set).slice(-200);
  localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(arr));
}

export function useEmailNotifications({ hasImapConfig }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(OPTIN_KEY) === "1");
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const lastSyncRef = useRef(0);

  const supported = typeof Notification !== "undefined" && "serviceWorker" in navigator;

  async function requestPermission() {
    if (!supported) return "denied";
    const p = await Notification.requestPermission();
    setPermission(p);
    if (p === "granted") {
      localStorage.setItem(OPTIN_KEY, "1");
      setEnabled(true);
      if (!localStorage.getItem(LAST_CHECK_KEY)) {
        localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
      }
    }
    return p;
  }

  function disable() {
    localStorage.setItem(OPTIN_KEY, "0");
    setEnabled(false);
  }

  // Poll for new notifications
  useQuery({
    queryKey: ["email-notifications", enabled, hasImapConfig],
    queryFn: async () => {
      const since = localStorage.getItem(LAST_CHECK_KEY) || new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const { data } = await api.get(`/email/notifications?since=${encodeURIComponent(since)}`);

      // Auto-sync every 5 min so new mail actually arrives from the IMAP server.
      if (Date.now() - lastSyncRef.current > AUTO_SYNC_MS) {
        lastSyncRef.current = Date.now();
        api.post("/email/sync").catch(() => {});
      }

      const seen = getSeenIds();
      const fresh = (data.items || []).filter((m) => !seen.has(m.id));
      for (const m of fresh) {
        seen.add(m.id);
        showNotification(m);
      }
      if (fresh.length) saveSeenIds(seen);
      localStorage.setItem(LAST_CHECK_KEY, data.now);
      return data;
    },
    enabled: enabled && hasImapConfig && permission === "granted",
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
  });

  return { enabled, permission, supported, requestPermission, disable };
}

function showNotification(m) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const title = m.likely_appointment
    ? `Neue Anfrage von ${m.from_name || m.from_email}`
    : `Neue E-Mail von ${m.from_name || m.from_email}`;
  const body = m.subject || "(kein Betreff)";
  try {
    // Use SW registration so notifications survive tab close (when available).
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, {
          body,
          icon: "/logo192.svg",
          badge: "/logo192.svg",
          tag: `stellar-email-${m.id}`,
          data: { url: "/emails" },
          requireInteraction: !!m.likely_appointment,
        }).catch(() => {
          new Notification(title, { body, icon: "/logo192.svg" });
        });
      });
    } else {
      new Notification(title, { body, icon: "/logo192.svg" });
    }
  } catch {
    // Notifications may be disabled by system settings — silently ignore.
  }
}
